import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * `<product-more-details>` — the "More Details" link under the product page's
 * SEO phrase (Figma node 6400:93593).
 *
 * The link is a shortcut to the first row of the product details accordion
 * ("Details" in templates/product.json): it opens that row and scrolls it to
 * the top of the page, just below the header. It is deliberately not an
 * `<a href="#row">` — `_accordion-row` gives its `<details>` no id, and a
 * fragment link could not open the row anyway.
 *
 * The row is found rather than configured. A block cannot reference a sibling
 * block's generated id from Liquid, and the design only ever points at the
 * first row, so "the first `<details>` in this column's accordion" is the whole
 * contract. Nothing is rendered differently when no accordion is present — the
 * click is simply a no-op, which is the same outcome as pointing at a row that
 * has been removed in the editor.
 */
class ProductMoreDetails extends Component {
  /**
   * Where the row's top edge is asked to land: this far below the header. The
   * same 15px the pinned details column keeps between itself and the header
   * (`--sticky-header-offset` in blocks/_product-details.liquid).
   */
  static #GAP = 15;

  /**
   * The row the link opens.
   *
   * Scoped to the product details column so the target can only ever be the
   * accordion sitting alongside this link — the PDP renders other `<details>`
   * elements (filters, drawers, the header's own menus) and a quick-add modal
   * renders a second copy of this whole column.
   *
   * @returns {HTMLDetailsElement | null}
   */
  get #row() {
    const scope = this.closest('.product-details') ?? this.closest('.shopify-section');
    const row = scope?.querySelector('.accordion details');

    return row instanceof HTMLDetailsElement ? row : null;
  }

  /**
   * Opens the Details row and scrolls it to the top. Bound from the link with
   * `on:click`.
   */
  openDetails() {
    const row = this.#row;
    if (!row) return;

    // Already-open is a normal state: the shopper may have opened the row by
    // hand before clicking the link. The scroll still runs, so the link always
    // ends with the row at the top of the page.
    row.open = true;

    const summary = row.querySelector('summary');

    // The summary rather than the row: the two share a top edge, and it is the
    // heading that has to come to rest below the header.
    const target = summary instanceof HTMLElement ? summary : row;

    this.#scrollTo(target);

    // Focus follows the shortcut so a keyboard or screen reader user carries on
    // from the row that just opened, and hears its expanded state. The scroll
    // above already placed it, hence `preventScroll`.
    if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
  }

  /**
   * Scrolls so `element`'s top edge sits `#GAP` below the header.
   *
   * The scroll container differs by breakpoint — `.page-wrapper` scrolls from
   * 990px up (base.css hides the document's overflow there), the document below
   * it — so this works in deltas, which mean the same thing to both.
   *
   * @param {HTMLElement} element
   */
  #scrollTo(element) {
    const delta = this.#scrollDelta(element);
    if (Math.abs(delta) < 1) return;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    const scroller = this.#scroller;

    if (scroller) {
      scroller.scrollTo({ top: scroller.scrollTop + delta, behavior });
    } else {
      window.scrollTo({ top: window.scrollY + delta, behavior });
    }
  }

  /**
   * How far to scroll to bring `element` to rest below the header.
   *
   * On mobile that is just the distance it has to travel. On desktop the row
   * rides inside the pinned details column (`.product-details--sticky-last-block`
   * pins the last block of the column), and a pinned block does not move with
   * the page — scrolling by that distance slides the gallery past and leaves the
   * row exactly where it was, which is the bug this replaced.
   *
   * What does move a pinned block is the bottom edge of its containing block
   * catching up with it: from there on it travels with the page again. So the
   * scroll aims that edge instead. Put the containing block's bottom at
   * `wanted - offsetWithin + height` and the pinned block's own top lands at
   * `wanted - offsetWithin`, which is exactly where the row sits at `wanted`.
   *
   * Overshooting the page's own scroll range is fine and needs no clamp of its
   * own: both scroll APIs clamp, and the row then comes as close to the top as
   * the page allows.
   *
   * @param {HTMLElement} element
   * @returns {number}
   */
  #scrollDelta(element) {
    const wanted = this.#headerOffset + ProductMoreDetails.#GAP;
    const top = element.getBoundingClientRect().top;

    const pinned = this.#pinnedAncestor(element);
    const container = pinned?.parentElement;
    if (!pinned || !container) return top - wanted;

    // Fixed for the life of the layout: where the row sits inside the block
    // that pins, which is what the pin makes independent of the scroll.
    const offsetWithin = top - pinned.getBoundingClientRect().top;

    return this.#contentBottom(container) - (wanted - offsetWithin + pinned.offsetHeight);
  }

  /**
   * The nearest `position: sticky` ancestor of `element` within this product
   * section, or null when nothing between them pins.
   *
   * The walk stops at the section because the only sticky box above it is the
   * header, which this already accounts for as an offset.
   *
   * @param {HTMLElement} element
   * @returns {HTMLElement | null}
   */
  #pinnedAncestor(element) {
    const section = this.closest('.shopify-section');

    for (let node = element.parentElement; node && node !== section; node = node.parentElement) {
      if (getComputedStyle(node).position === 'sticky') return node;
    }

    return null;
  }

  /**
   * The viewport offset of `element`'s content-box bottom — the edge a sticky
   * child is held inside, and so the one the scroll above aims at. Its border
   * box is what `getBoundingClientRect` reports.
   *
   * @param {HTMLElement} element
   * @returns {number}
   */
  #contentBottom(element) {
    const styles = getComputedStyle(element);
    const inset = parseFloat(styles.borderBottomWidth) + parseFloat(styles.paddingBottom);

    return element.getBoundingClientRect().bottom - (Number.isFinite(inset) ? inset : 0);
  }

  /** The scrolling ancestor, or null when the document itself scrolls. */
  get #scroller() {
    const wrapper = this.closest('.page-wrapper');
    if (!(wrapper instanceof HTMLElement)) return null;

    return getComputedStyle(wrapper).overflowY === 'auto' ? wrapper : null;
  }

  /**
   * How much of the viewport top the header covers. Mirrors the reasoning in
   * hero-slider.js: header.js keeps `--header-height` on <body> in sync with
   * the header's rendered height, and only an `always` sticky header is over
   * the content once the page has been scrolled.
   *
   * @returns {number}
   */
  get #headerOffset() {
    if (!document.querySelector('header-component[sticky="always"]')) return 0;

    const height = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));

    return Number.isFinite(height) ? height : 0;
  }
}

if (!customElements.get('product-more-details')) {
  customElements.define('product-more-details', ProductMoreDetails);
}
